import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isSameDay } from 'date-fns';
import type AbilitiesService from 'ember-can/services/abilities';
import type { Task } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency';
import type ListItemCheckbox from 'garaje/components/list-item-checkbox';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type EntryModel from 'garaje/models/entry';
import type InviteModel from 'garaje/models/invite';
import type LocationModel from 'garaje/models/location';
import type { Field } from 'garaje/models/location';
import type PrinterModel from 'garaje/models/printer';
import type ReservationModel from 'garaje/models/reservation';
import type SubscriptionModel from 'garaje/models/subscription';
import type VisitorProfileModel from 'garaje/models/visitor-profile';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import { isSensitiveFieldMasked, type SensitiveState } from 'garaje/services/sensitive-fields-visibility';
import type SensitiveFieldsVisibilityService from 'garaje/services/sensitive-fields-visibility';
import type StateService from 'garaje/services/state';
import type UploaderFactoryService from 'garaje/services/uploader-factory';
import type WindowLocationService from 'garaje/services/window-location';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { createBlocklistFilter, saveBlocklistFilter } from 'garaje/utils/blocklist-utils';
import { canSignOutEntry } from 'garaje/utils/entry-sign-out-permission';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { shouldShowVisitorProfileHoverCard } from 'garaje/utils/visitor-profile-hover-card';
import zft from 'garaje/utils/zero-for-tests';
import { alias, reads, or } from 'macro-decorators';
import { defer } from 'rsvp';

interface EntryFeedItemComponentSignature {
  Args: {
    checkbox: ListItemCheckbox<unknown>;
    checkID: () => unknown;
    context: 'location' | 'property';
    deleteEntry: () => unknown;
    entry: EntryModel;
    eventReports: unknown[];
    fieldOptions: Field[];
    fullDate: boolean;
    hasPhoto: boolean;
    isSelected: boolean;
    location: LocationModel;
    loadEntries: Task<unknown, []>;
    printers: PrinterModel[];
    reprintBadge: (entry: EntryModel, printer: PrinterModel) => unknown;
    selectedDate: Date;
    showSignedOut: boolean;
    signOutTask: Task<unknown, []>;
    vrSubscription: SubscriptionModel;
    linkToRoute: string;
    renderStickyShadows?: boolean;
    userType: string;
    additionalFieldsToExclude?: string[];
  };
}

export default class EntryFeedItemComponent extends Component<EntryFeedItemComponentSignature> {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare sensitiveFieldsVisibility: SensitiveFieldsVisibilityService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare windowLocation: WindowLocationService;
  @service declare workplaceMetrics: WorkplaceMetricsService;
  @service declare uploaderFactory: UploaderFactoryService;
  @service declare flashMessages: FlashMessagesService;

  @tracked displayPrinterSelectorModal = false;

  @tracked itemFullNameWasTruncated = false;
  @tracked itemHostWasTruncated = false;

  @tracked blocklistFilterInModal: BlacklistFilter | null = null;

  tooltipDelay = zft(400);

  // The visitor-name cell repeats the host as a subtitle. Mask it with the same
  // rule (and the same reveal key) the dedicated Host column uses, otherwise a
  // masked Host column leaks the host name here in clear text. See HostColumn.
  get hostSensitiveState(): SensitiveState | undefined {
    return this.args.entry.sensitiveFields?.['Host'];
  }

  get hostIsMasked(): boolean {
    return isSensitiveFieldMasked(this.hostSensitiveState, this.sensitiveFieldsVisibility.isRevealed('Host'));
  }

  get displayHost(): string {
    if (this.hostIsMasked) {
      return 'Hidden';
    }

    return this.args.entry.host ?? '';
  }

  @alias('state.currentCompany') currentCompany!: StateService['currentCompany'];
  @alias('args.vrSubscription.canEnableBadgePrinting') canReprintBadge!: SubscriptionModel['canEnableBadgePrinting'];
  @alias('args.entry.photoUrl') invitePhotoUrl!: InviteModel['photoUrl'];
  @reads('currentAdmin.canDeleteVisitor') canDeleteVisitor!: CurrentAdminService['canDeleteVisitor'];
  @or('canReferToEnvoy', 'canReprintBadge', 'shouldShowSignOutVisitor', 'canDeleteVisitor', 'canManageReservation')
  menuHasActions!: boolean;

  @or('args.entry.needsApprovalReview', 'args.entry.approvalWasDenied') visitorIsDisallowedAccess!: boolean;

  get reviewerName(): string | undefined {
    return this.args.entry.visitApproval?.screenerReviews.slice()[0]?.reviewerName;
  }

  get canManageReservation(): boolean {
    if (this.isForVisitor) {
      return false;
    }

    return this.abilities.can('edit reservation') || this.abilities.can('delete reservation');
  }

  // colspan for the denied message is equal the the number of shown fieldOptions minus 1 for the name field, which is already in the row and is not included in the colspan
  get deniedColspan(): number {
    return (this.args.fieldOptions ?? []).filter((field) => field.show).length - 1;
  }

  /**
   * Permission check (no state). Workplace/employee log keeps the legacy
   * edit-based logic. For visitor entries, when the restriction is on, only the
   * explicit sign-out permission authorizes; otherwise edit OR sign-out authorizes.
   */
  get hasSignOutVisitorPermission(): boolean {
    const entry = this.args.entry;
    const canEdit = this.abilities.can('edit entry', entry);
    const canSignOut = this.abilities.can('sign-out entry');

    if (this.args.userType !== 'visitor') return canEdit;

    return canSignOutEntry({
      canEdit,
      canSignOut,
      location: this.args.location,
    });
  }

  /**
   * State checks: whether a sign-out action should be offered for this entry at all.
   * For visitor entries, render the item (possibly disabled) so the explanatory
   * tooltip can surface elsewhere. For workplace/employee entries, only show the
   * overflow item when the user actually has permission.
   */
  get shouldShowSignOutVisitor(): boolean {
    const entry = this.args.entry;
    if (entry.anonymizedAt) return false;
    if (!entry.signInTime) return false;
    if (entry.signedOutAt) return false;
    if (entry.approvalWasDenied) return false;
    if (this.args.userType !== 'visitor') return this.hasSignOutVisitorPermission;
    return true;
  }

  get canSignOutVisitor(): boolean {
    return this.shouldShowSignOutVisitor && this.hasSignOutVisitorPermission;
  }

  get cannotReviewEntry(): boolean {
    return this.abilities.cannot('review entry-approval', {
      context: this.args.context ?? 'location',
      visitApproval: this.args.entry.visitApproval,
      parent: this.args.entry,
    });
  }

  get canInviteAgain(): boolean {
    return (
      !this.args.entry.isFromEmployeeScreening &&
      !this.args.entry.isFromWalkUp &&
      !this.visitorIsDisallowedAccess &&
      !this.args.entry.anonymizedAt
    );
  }

  get visitorEmail(): string | undefined {
    return this.getUserDataField(this.args.entry, 'Your Email Address');
  }

  get visitorFullName(): string | undefined {
    return this.getUserDataField(this.args.entry, 'Your Full Name');
  }

  get canReferToEnvoy(): boolean {
    return !!this.visitorEmail;
  }

  get referralCode(): string {
    return `c${this.currentCompany.id}u${this.currentAdmin.id}`;
  }

  get referralUrl(): string {
    return urlBuilder.signupReferralUrl(this.referralCode);
  }

  get isWorkplaceRoute(): boolean {
    return this.router.isActive('workplace');
  }

  get referralMailtoLink(): string {
    const subject = `Interested in ${this.currentCompany.name}'s visitor sign-in system?`;
    const bcc = 'referrals@envoy.com';
    const body = `

      Hi ${this.visitorFullName},

      When you signed in on the iPad today, you experienced Envoy — our digital sign in system.

      If you're interested in trying Envoy for yourself, just visit this link to sign up: ${this.referralUrl}

      Once you sign up, email referrals@envoy.com to connect with the Envoy team and claim your special referral discount.

      Best,
      ${this.currentAdmin.fullName}
      ${this.currentCompany.name}

    `
      .trim()
      .replace(/\n +/g, '\n')
      .replace(/\n/g, '%0D%0A');
    return `mailto:${this.visitorEmail}?subject=${subject}&bcc=${bcc}&body=${body}`;
  }

  get showInDate(): boolean {
    const selectedDate = this.args.selectedDate;
    const signedInAt = this.args.entry.signedInAt;

    // if the selected date is not passed in, we are looking at a range and want to see dates
    if (!selectedDate) return true;

    return !isSameDay(signedInAt, selectedDate);
  }

  get showOutDate(): boolean {
    const selectedDate = this.args.selectedDate;
    const signedOutAt = this.args.entry.signedOutAt;

    // if the selected date is not passed in, we are looking at a range and want to see dates
    if (!selectedDate) return true;

    return !isSameDay(signedOutAt, selectedDate);
  }

  get isForVisitor(): boolean {
    return this.args.userType === 'visitor';
  }

  get visitorProfile(): VisitorProfileModel | null {
    // Defensive .value?.() — see invite-feed-item.gts:156 for rationale + autotracking caveat.
    return (this.args.entry.belongsTo?.('visitorProfile')?.value?.() ?? null) as VisitorProfileModel | null;
  }

  get showHoverCard(): boolean {
    return shouldShowVisitorProfileHoverCard({
      visitorProfile: this.visitorProfile,
      recordId: this.args.entry?.id,
      featureFlags: this.featureFlags,
      abilities: this.abilities,
    });
  }

  get showEditOption(): boolean {
    const featureEnabled = this.abilities.can('see features for dbeam');
    return featureEnabled && !this.isForVisitor && !this.entry.signedInAt && this.abilities.can('edit reservation');
  }

  get showLegacyReservationOptions(): boolean {
    const featureFlagEnabled = this.abilities.can('see features for dbeam');
    return !featureFlagEnabled || this.isForVisitor;
  }

  get canDeleteReservation(): boolean {
    const featureFlagEnabled = this.abilities.can('see features for dbeam');
    return featureFlagEnabled && !this.isForVisitor && this.abilities.can('delete reservation');
  }

  get entry(): EntryModel {
    return this.args.entry;
  }

  get entryReservation(): ReservationModel | undefined {
    return this.args.entry.reservations?.content?.[0];
  }

  getUserDataField(entry: EntryModel, fieldName: string): string | undefined {
    const userData = entry.userData ?? [];
    const userDataField = userData.find((data) => data.field === fieldName);
    return userDataField?.value;
  }

  @action
  trackOpenActionMenu(entry_id: string): void {
    this.metrics.trackEvent('Entry Overflow Opened', { entry_id });
  }

  trackReprintBadgeRequested(entry: EntryModel): void {
    const entry_id = parseInt(entry.id, 10);
    const properties = { action_origin: 'visitor_log', entry_id };
    this.metrics.trackEvent('Reprint Entry Badge Requested', properties);
  }

  @action
  trackClickEntryDetail(entryId: string): void {
    this.metrics.trackEvent('Entry Details Clicked', { entry_id: entryId });
  }

  @action
  referToEnvoy(): void {
    this.windowLocation.assign(this.referralMailtoLink);
  }

  @action
  reprintBadge(entry: EntryModel, printer: PrinterModel): void {
    this.args.reprintBadge(entry, printer);
    this.trackReprintBadgeRequested(entry);
  }

  @action
  inviteAgain(entry: EntryModel): void {
    const { email, fullName: name } = entry;
    const previousInvite = entry.belongsTo('invite').id();

    void this.router.transitionTo('visitors.invites.new', {
      queryParams: { email, name, previousInvite, returnTo: 'visitors.entries' },
    });

    this.metrics.trackEvent('Entries Invited Again', { invite_id: previousInvite });
  }

  @action
  createBlocklistFilter(): void {
    this.blocklistFilterInModal = createBlocklistFilter({
      model: this.args.entry,
      store: this.store,
      location: this.state.currentLocation,
      useLocationScoping: this.featureFlags.isEnabled('feat-blocklist-location-scoping'),
    });

    this.metrics.startJob();

    this.metrics.trackJobEvent('Invite - Add to Block List Button Clicked', {
      entry_id: this.args.entry.id,
    });
  }

  saveBlocklistFilterTask = dropTask(
    waitFor(async (blocklistFilter: BlacklistFilter, photo: File | null) => {
      try {
        await saveBlocklistFilter({
          blocklistFilter,
          failOnPhotoFetchError: this.featureFlags.isEnabled('blocklist-fail-on-photo-fetch-fail'),
          photo,
          uploaderFactory: this.uploaderFactory,
        });
        this.blocklistFilterInModal = null;
        this.flashMessages.showAndHideFlash('success', 'Blocklist filter saved');
      } catch (e) {
        this.flashMessages.showFlash('error', parseErrorForDisplay(e));
      }
    }),
  );

  @dropTask
  reservationModalTask: {
    perform(): Generator<Promise<unknown>, unknown, unknown>;
  } = {
    *perform(this: { context: EntryFeedItemComponent; abort?: () => void; continue?: () => void }) {
      this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_EDIT_RESERVATION_LINK_CLICKED', {
        entryId: this.context.entry?.id,
        reservationId: this.context.entryReservation?.id,
      });

      const deferred = defer();
      this.abort = () => {
        this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_EDIT_RESERVATION_FLOW_ABORTED', {
          entryId: this.context.entry?.id,
          reservationId: this.context.entryReservation?.id,
        });

        deferred.resolve(false);
      };
      this.continue = () => {
        void this.context.args.loadEntries.perform();

        this.context.workplaceMetrics.trackEvent('EMPLOYEE_LOG_EDIT_RESERVATION_FLOW_COMPLETED', {
          entryId: this.context.entry?.id,
          reservationId: this.context.entryReservation?.id,
        });

        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };
}
